import { OverallCourseVerdict } from '@shared/passing-policy/passing-policy';

/**
 * KAN-411 - display copy for the OVERALL course verdict (the fold of the
 * per-unit results with the course aggregate, `computeOverallCourseVerdict`).
 *
 * Every surface that shows a course pass/fail renders these, so the learner
 * banner and the two instructor readouts cannot state different verdicts for
 * the same learner. The verdict itself is decided in repo-root shared/; this
 * file only names it.
 */

export interface CourseVerdictBadge {
  label: string;
  /** DaisyUI badge modifier class. */
  cssClass: string;
}

/** Instructor-facing badge, matching the unit rows directly beneath it. */
export function courseVerdictBadge(status: OverallCourseVerdict['status']): CourseVerdictBadge {
  switch (status) {
    case 'passed':
      return { label: 'Passed', cssClass: 'badge-success' };
    case 'failed':
      return { label: 'Failed', cssClass: 'badge-error' };
    case 'pending':
      return { label: 'Pending', cssClass: 'badge-warning' };
    case 'in_progress':
      return { label: 'In progress', cssClass: 'badge-ghost' };
    default:
      return { label: '', cssClass: 'badge-ghost' };
  }
}

/** DaisyUI alert modifier for the learner's course-summary banner. */
export function courseAlertClass(status: OverallCourseVerdict['status']): string {
  switch (status) {
    case 'passed':
      return 'alert-success';
    case 'failed':
      return 'alert-error';
    case 'pending':
      return 'alert-warning';
    default:
      return '';
  }
}

/**
 * The learner banner headline - a VERDICT-ONLY announcement in learner words.
 *
 * KAN-503: the banner used to read "Course aggregate: 92.9% - Not passed",
 * which sat next to the KAN-465 running score ("Running score: 18/20 pts
 * (90%)") - two percentages with different denominators (the running score
 * counts only attempted quizzes; the aggregate counts every quiz), so the pair
 * read as a contradiction. The banner now states only the verdict: no
 * percentage, no points, no "aggregate". The KAN-411 status mapping is
 * unchanged, so a gate-blocked course still never claims a pass, and "not
 * passed yet" keeps reading softer than "Failed".
 */
export function courseVerdictHeadline(verdict: OverallCourseVerdict): string {
  switch (verdict.status) {
    case 'passed':
      return 'You passed this course';
    case 'failed':
      return 'Course not passed yet';
    case 'pending':
      return 'Course result pending';
    case 'in_progress':
      return 'Course in progress';
    default:
      return '';
  }
}

function countPhrase(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Why the course is blocked while its aggregate clears the threshold - the case
 * that made the old banner lie ("92.9% - Passed" above a blocked completion).
 *
 * Returns null when the headline already tells the whole story: a course that is
 * passing, pending, in progress, or short on the aggregate itself needs no
 * extra sentence. The per-quiz specifics are already listed beneath the banner
 * (KAN-410 floor rows), so this names the class of block, not every instance.
 *
 * KAN-503: reworded to learner language. The lead says "your overall score"
 * instead of quoting the threshold percentage, and the floor reason says
 * "minimum score" rather than "minimum points", so the banner carries no
 * numeric score at all. The distinct block classes (floor / gate / unit) are
 * preserved one-to-one.
 */
export function courseBlockReason(verdict: OverallCourseVerdict | null): string | null {
  if (!verdict || verdict.status !== 'failed' || !verdict.courseAggregateMet) return null;

  const reasons: string[] = [];
  if (verdict.floorViolations.length) {
    reasons.push(
      `${countPhrase(verdict.floorViolations.length, 'quiz is', 'quizzes are')} below the required minimum score`
    );
  }
  if (verdict.gateViolations.length) {
    reasons.push(`${countPhrase(verdict.gateViolations.length, 'quiz was', 'quizzes were')} not passed`);
  }
  if (!reasons.length && verdict.failedUnitCount) {
    reasons.push(
      `${countPhrase(verdict.failedUnitCount, 'unit did', 'units did')} not meet the unit passing score`
    );
  }
  if (!reasons.length) return null;

  return `Your overall score meets the course requirement, but ${reasons.join(' and ')}. See the details below.`;
}
